import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Badge,
  LinearProgress,
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Visibility,
  VisibilityOff,
  Refresh,
  CheckCircle,
  Group,
  Timer,
  EmojiEvents,
  TrendingUp,
  TrendingDown,
  TrendingFlat,
  Close,
  Share,
  ContentCopy,
} from '@mui/icons-material';
import { io, Socket } from 'socket.io-client';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface PlanningPokerCard {
  value: number | string;
  label: string;
  description?: string;
}

interface PlanningPokerVote {
  userId: string;
  userName: string;
  userEmail: string;
  cardValue: number | string;
  timestamp: Date;
}

interface PlanningPokerSession {
  id: string;
  roomCode: string;
  issueId: string;
  issueTitle: string;
  issueDescription: string;
  facilitatorId: string;
  facilitatorName: string;
  participants: Array<{
    userId: string;
    userName: string;
    userEmail: string;
    joinedAt: Date;
  }>;
  votes: PlanningPokerVote[];
  isRevealed: boolean;
  consensus?: {
    average: number;
    median: number;
    mode: number;
    agreement: 'high' | 'medium' | 'low';
  };
  createdAt: Date;
  updatedAt: Date;
}

interface PlanningPokerRoomProps {
  issueId: string;
  issueTitle: string;
  issueDescription?: string;
  onClose: () => void;
  onEstimationComplete?: (estimate: number) => void;
}

const PlanningPokerRoom: React.FC<PlanningPokerRoomProps> = ({
  issueId,
  issueTitle,
  issueDescription,
  onClose,
  onEstimationComplete,
}) => {
  const { user } = useAuth();
  const [session, setSession] = useState<PlanningPokerSession | null>(null);
  const [cards, setCards] = useState<PlanningPokerCard[]>([]);
  const [selectedCard, setSelectedCard] = useState<number | string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [roomCode, setRoomCode] = useState('');
  const [isFacilitator, setIsFacilitator] = useState(false);
  const [finalEstimate, setFinalEstimate] = useState<number>(0);

  // Initialize socket connection
  useEffect(() => {
    const newSocket = io(process.env.REACT_APP_API_URL || 'http://localhost:4000');
    setSocket(newSocket);

    // Socket event listeners
    newSocket.on('participant-joined', (data) => {
      setSession(prev => prev ? { ...prev, participants: data.participants } : null);
    });

    newSocket.on('participant-left', (data) => {
      setSession(prev => prev ? { ...prev, participants: data.participants } : null);
    });

    newSocket.on('vote-submitted', (data) => {
      // Update UI to show vote count
      console.log('Vote submitted:', data);
    });

    newSocket.on('votes-revealed', (data) => {
      setSession(prev => prev ? { 
        ...prev, 
        votes: data.votes, 
        isRevealed: true,
        consensus: data.consensus 
      } : null);
    });

    newSocket.on('votes-reset', () => {
      setSession(prev => prev ? { 
        ...prev, 
        votes: [], 
        isRevealed: false,
        consensus: undefined 
      } : null);
      setSelectedCard(null);
    });

    newSocket.on('estimation-finalized', (data) => {
      onEstimationComplete?.(data.finalEstimate);
      onClose();
    });

    newSocket.on('session-ended', () => {
      onClose();
    });

    return () => {
      newSocket.disconnect();
    };
  }, [onClose, onEstimationComplete]);

  // Load cards and create session
  useEffect(() => {
    const initializeSession = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load Fibonacci cards
        const cardsResponse = await apiService.get<PlanningPokerCard[]>('/planning-poker/cards');
        if (cardsResponse.success && cardsResponse.data) {
          setCards(cardsResponse.data);
        }

        // Create new session
        const sessionResponse = await apiService.post<PlanningPokerSession>('/planning-poker/sessions', {
          issueId
        });

        if (sessionResponse.success && sessionResponse.data) {
          setSession(sessionResponse.data);
          setIsFacilitator(sessionResponse.data.facilitatorId === user?.id);
          
          // Join socket room
          if (socket) {
            socket.emit('join-planning-poker', sessionResponse.data.id);
          }
        } else {
          setError('Failed to create planning poker session');
        }
      } catch (err: any) {
        console.error('Error initializing session:', err);
        setError(err.response?.data?.message || 'Failed to initialize session');
      } finally {
        setLoading(false);
      }
    };

    initializeSession();
  }, [issueId, user?.id, socket]);

  const handleCardSelect = async (cardValue: number | string) => {
    if (!session || !socket) return;

    setSelectedCard(cardValue);

    try {
      await apiService.post(`/planning-poker/sessions/${session.id}/vote`, {
        cardValue
      });
    } catch (err: any) {
      console.error('Error submitting vote:', err);
      setError('Failed to submit vote');
    }
  };

  const handleRevealVotes = async () => {
    if (!session) return;

    try {
      await apiService.post(`/planning-poker/sessions/${session.id}/reveal`);
    } catch (err: any) {
      console.error('Error revealing votes:', err);
      setError('Failed to reveal votes');
    }
  };

  const handleResetVotes = async () => {
    if (!session) return;

    try {
      await apiService.post(`/planning-poker/sessions/${session.id}/reset`);
    } catch (err: any) {
      console.error('Error resetting votes:', err);
      setError('Failed to reset votes');
    }
  };

  const handleFinalizeEstimation = async () => {
    if (!session || finalEstimate <= 0) return;

    try {
      await apiService.post(`/planning-poker/sessions/${session.id}/finalize`, {
        finalEstimate
      });
    } catch (err: any) {
      console.error('Error finalizing estimation:', err);
      setError('Failed to finalize estimation');
    }
  };

  const handleJoinSession = async () => {
    if (!roomCode.trim()) return;

    try {
      setLoading(true);
      const response = await apiService.post<PlanningPokerSession>('/planning-poker/sessions/join', {
        roomCode: roomCode.trim()
      });

      if (response.success && response.data) {
        setSession(response.data);
        setIsFacilitator(response.data.facilitatorId === user?.id);
        
        if (socket) {
          socket.emit('join-planning-poker', response.data.id);
        }
        
        setShowJoinDialog(false);
      } else {
        setError('Failed to join session');
      }
    } catch (err: any) {
      console.error('Error joining session:', err);
      setError(err.response?.data?.message || 'Failed to join session');
    } finally {
      setLoading(false);
    }
  };

  const copyRoomCode = () => {
    if (session?.roomCode) {
      navigator.clipboard.writeText(session.roomCode);
    }
  };

  const getAgreementColor = (agreement: string) => {
    switch (agreement) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'error';
      default: return 'default';
    }
  };

  const getAgreementIcon = (agreement: string) => {
    switch (agreement) {
      case 'high': return <TrendingUp />;
      case 'medium': return <TrendingFlat />;
      case 'low': return <TrendingDown />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button onClick={() => setError(null)} sx={{ ml: 2 }}>
          Dismiss
        </Button>
      </Alert>
    );
  }

  if (!session) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Planning Poker
        </Typography>
        <Button
          variant="contained"
          onClick={() => setShowJoinDialog(true)}
          startIcon={<Group />}
        >
          Join Existing Session
        </Button>
        
        <Dialog open={showJoinDialog} onClose={() => setShowJoinDialog(false)}>
          <DialogTitle>Join Planning Poker Session</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Room Code"
              fullWidth
              variant="outlined"
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
              placeholder="Enter 6-character room code"
              inputProps={{ maxLength: 6 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowJoinDialog(false)}>Cancel</Button>
            <Button onClick={handleJoinSession} variant="contained">
              Join
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Planning Poker Session
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Chip
              label={`Room: ${session.roomCode}`}
              color="primary"
              icon={<Share />}
              onClick={copyRoomCode}
            />
            <Chip
              label={`${session.participants.length} participants`}
              color="secondary"
              icon={<Group />}
            />
            {isFacilitator && (
              <Chip
                label="Facilitator"
                color="success"
                icon={<EmojiEvents />}
              />
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose}>
          <Close />
        </IconButton>
      </Box>

      {/* Issue Details */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {session.issueTitle}
          </Typography>
          {session.issueDescription && (
            <Typography variant="body2" color="text.secondary">
              {session.issueDescription}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Participants */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Participants
          </Typography>
          <Grid container spacing={1}>
            {session.participants.map((participant) => {
              const hasVoted = session.votes.some(vote => vote.userId === participant.userId);
              const userVote = session.votes.find(vote => vote.userId === participant.userId);
              
              return (
                <Grid item key={participant.userId}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Badge
                      color={hasVoted ? 'success' : 'default'}
                      variant="dot"
                    >
                      <Avatar sx={{ width: 32, height: 32 }}>
                        {participant.userName.split(' ').map(n => n[0]).join('')}
                      </Avatar>
                    </Badge>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {participant.userName}
                      </Typography>
                      {hasVoted && session.isRevealed && (
                        <Chip
                          label={userVote?.cardValue}
                          size="small"
                          color="primary"
                        />
                      )}
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Voting Cards */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select Your Estimate
          </Typography>
          <Grid container spacing={1}>
            {cards.map((card) => (
              <Grid item key={card.value}>
                <Button
                  variant={selectedCard === card.value ? 'contained' : 'outlined'}
                  color={selectedCard === card.value ? 'primary' : 'inherit'}
                  onClick={() => handleCardSelect(card.value)}
                  sx={{
                    minWidth: 60,
                    height: 80,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                  }}
                  disabled={session.isRevealed}
                >
                  <Typography variant="h6">{card.label}</Typography>
                  {card.description && (
                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                      {card.description}
                    </Typography>
                  )}
                </Button>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Facilitator Controls */}
      {isFacilitator && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Facilitator Controls
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="contained"
                startIcon={session.isRevealed ? <VisibilityOff /> : <Visibility />}
                onClick={handleRevealVotes}
                disabled={session.votes.length === 0}
              >
                {session.isRevealed ? 'Hide Votes' : 'Reveal Votes'}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={handleResetVotes}
                disabled={session.votes.length === 0}
              >
                Reset Votes
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Consensus Results */}
      {session.isRevealed && session.consensus && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Estimation Results
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="primary">
                    {session.consensus.average}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Average
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="secondary">
                    {session.consensus.median}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Median
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="h4" color="info">
                    {session.consensus.mode}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mode
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
            
            <Box mt={2} display="flex" justifyContent="center">
              <Chip
                label={`Agreement: ${session.consensus.agreement}`}
                color={getAgreementColor(session.consensus.agreement) as any}
                icon={getAgreementIcon(session.consensus.agreement) || undefined}
                size="medium"
              />
            </Box>

            {/* Finalize Estimation */}
            {isFacilitator && (
              <Box mt={3}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Finalize Estimation
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <TextField
                    type="number"
                    label="Final Estimate"
                    value={finalEstimate}
                    onChange={(e) => setFinalEstimate(Number(e.target.value))}
                    inputProps={{ min: 1 }}
                    sx={{ width: 150 }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<CheckCircle />}
                    onClick={handleFinalizeEstimation}
                    disabled={finalEstimate <= 0}
                  >
                    Finalize
                  </Button>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Vote Progress */}
      {!session.isRevealed && (
        <Box mb={2}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Votes: {session.votes.length} / {session.participants.length}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(session.votes.length / session.participants.length) * 100}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}
    </Box>
  );
};

export default PlanningPokerRoom;
